import * as React from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface DatePickerProps {
  date: Date | string | null;
  setDate: (date: Date | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function DatePicker({
  date,
  setDate,
  placeholder = 'Select date',
  disabled = false,
}: DatePickerProps) {
  // Convert string date to Date object if needed
  const dateValue = React.useMemo(() => {
    if (!date) return null;
    if (date instanceof Date) return date;
    if (typeof date === 'string') {
      try {
        const parsed = new Date(date);
        // Check if date is valid
        return isNaN(parsed.getTime()) ? null : parsed;
      } catch {
        return null;
      }
    }
    return null;
  }, [date]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={'outline'}
          className={cn(
            'w-full justify-start text-left font-normal',
            !dateValue && 'text-muted-foreground'
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {dateValue ? format(dateValue, 'PPP') : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={dateValue || undefined}
          onSelect={(date) => setDate(date || null)}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
